/* Copyright (c) 2024 Xperi. All Rights Reserved. */

import {
    Configuration,
    ConfigurationParameters,
    DefaultApi,
    LastViewedPositionGetRequest,
    LastViewedPositionPutRequest,
    RecordingsOfferIdOptionsGetRequest,
    RecordingsOfferIdScheduleDeleteRequest,
    RecordingsOfferIdScheduleGetRequest,
    RecordingsOfferIdSchedulePostRequest,
    RecordingsOfferIdSchedulePutRequest,
    RecordingsOptionsGetRequest,
    RecordingsRecoveryPutRequest,
    RecordingsScheduleDeleteRequest,
    RecordingsScheduleGetRequest,
    RecordingsSchedulePutRequest,
} from 'client-code/recordings';

import { CommonHeaders } from '../CommonHeaders';
import { Constants } from '../constants';
import { IRecordingsServiceRepository } from '../interfaces';
import { Log } from '../logging/Log';
import {
    TRecordingOptions,
    TRecordingValues,
    TRecordingValuesWithDisplayOptions,
    TServicesParams,
} from '../types';

export class RecordingsServiceRepositoryImpl implements IRecordingsServiceRepository {
    private static TAG: string = 'RecordingsServiceRepositoryImpl';

    private mCommonHeaders: CommonHeaders;

    private mRecordingApi: DefaultApi;

    private mServiceParams: TServicesParams;

    public constructor(params: TServicesParams) {
        this.mServiceParams = params;
        this.mCommonHeaders = new CommonHeaders('RecordingOptionsOverlay');

        const oConfigParams: ConfigurationParameters = {
            basePath: this.mServiceParams.basePath,
            apiKey: this.mServiceParams.accessToken,
            baseOptions: {
                timeout: Constants.SERVICE_CONNECTION_TIMEOUT,
                headers: this.mCommonHeaders.headers,
            },
        };

        const configuration = new Configuration(oConfigParams);
        this.mRecordingApi = new DefaultApi(configuration);
    }

    private getCommonRequestParams() {
        return {
            applicationName: this.mCommonHeaders.headers.ApplicationName,
            applicationFeatureArea: this.mCommonHeaders.headers.ApplicationFeatureArea,
            applicationVersion: this.mCommonHeaders.headers.ApplicationVersion,
            deviceType: this.mCommonHeaders.headers.DeviceType,
            productName: this.mCommonHeaders.headers.ProductName,
            bodyId: this.mServiceParams.bodyId,
            timeZoneName: this.mServiceParams.timeZone,
            whiny: this.mServiceParams.whiny || 'false',
        };
    }

    /**
     * @inheritdoc
     */
    public async cancelRecordingOfferIdSchedule(contentId: string, offerId: string) {
        const requestParameters: RecordingsOfferIdScheduleDeleteRequest = {
            ...this.getCommonRequestParams(),
            contentId,
            offerId,
        };

        try {
            return await this.mRecordingApi.recordingsOfferIdScheduleDelete(requestParameters);
        } catch (error: any) {
            Log.e(
                RecordingsServiceRepositoryImpl.TAG,
                'RecordingsServiceRepositoryImpl::cancelRecordingOfferIdSchedule',
                error,
            );
            throw error;
        }
    }

    /**
     * @inheritdoc
     */
    public async createRecordingOfferIdSchedule(
        contentId: string,
        offerId: string,
        tRecordingValues: TRecordingValues,
    ) {
        const requestParameters: RecordingsOfferIdSchedulePostRequest = {
            ...this.getCommonRequestParams(),
            contentId,
            offerId,
            recordingValues: tRecordingValues.recordingValues,
        };

        try {
            return await this.mRecordingApi.recordingsOfferIdSchedulePost(requestParameters);
        } catch (error: any) {
            Log.e(
                RecordingsServiceRepositoryImpl.TAG,
                'RecordingsServiceRepositoryImpl::createRecordingOfferIdSchedule',
                error,
            );
            throw error;
        }
    }

    /**
     * @inheritdoc
     */
    public async deleteRecordingSchedule(recordingId: string) {
        const requestParameters: RecordingsScheduleDeleteRequest = {
            ...this.getCommonRequestParams(),
            recordingId,
        };

        try {
            return await this.mRecordingApi.recordingsScheduleDelete(requestParameters);
        } catch (error: any) {
            Log.e(
                RecordingsServiceRepositoryImpl.TAG,
                'RecordingsServiceRepositoryImpl::deleteRecordingSchedule',
                error,
            );
            throw error;
        }
    }

    /**
     * @inheritdoc
     */
    public async fetchRecordingsLastViewedPosition(recordingId: string) {
        const requestParameters: LastViewedPositionGetRequest = {
            ...this.getCommonRequestParams(),
            recordingId,
        };

        try {
            return await this.mRecordingApi.lastViewedPositionGet(requestParameters);
        } catch (error: any) {
            Log.e(
                RecordingsServiceRepositoryImpl.TAG,
                'RecordingsServiceRepositoryImpl::lastViewedPositionGet',
                error,
            );
            throw error;
        }
    }

    /**
     * @inheritdoc
     */
    public async fetchOfferIdRecordingOptions(
        contentId: string,
        offerId: string,
    ): Promise<TRecordingOptions> {
        const requestParameters: RecordingsOfferIdOptionsGetRequest = {
            ...this.getCommonRequestParams(),
            contentId,
            offerId,
        };

        try {
            const response =
                await this.mRecordingApi.recordingsOfferIdOptionsGet(requestParameters);
            return { recordingOptions: response.data };
        } catch (error: any) {
            Log.e(
                RecordingsServiceRepositoryImpl.TAG,
                'RecordingsServiceRepositoryImpl::fetchOfferIdRecordingOptions',
                error,
            );
            throw error;
        }
    }

    /**
     * @inheritdoc
     */
    public async fetchRecordingValuesWithDisplayOptions(
        contentId: string,
        offerId: string,
    ): Promise<TRecordingValuesWithDisplayOptions> {
        const requestParameters: RecordingsOfferIdScheduleGetRequest = {
            ...this.getCommonRequestParams(),
            contentId,
            offerId,
        };

        try {
            const response =
                await this.mRecordingApi.recordingsOfferIdScheduleGet(requestParameters);
            return { recordingValuesWithDisplayOptions: response.data };
        } catch (error: any) {
            Log.e(
                RecordingsServiceRepositoryImpl.TAG,
                'RecordingsServiceRepositoryImpl::fetchRecordingValuesWithDisplayOptions',
                error,
            );
            throw error;
        }
    }

    /**
     * @inheritdoc
     */
    public async fetchRecordingIdRecordingOptions(
        contentId: string,
        recordingId: string,
    ): Promise<TRecordingOptions> {
        const requestParameters: RecordingsOptionsGetRequest = {
            ...this.getCommonRequestParams(),
            contentId,
            recordingId,
        };

        try {
            const response = await this.mRecordingApi.recordingsOptionsGet(requestParameters);
            return { recordingOptions: response.data };
        } catch (error: any) {
            Log.e(
                RecordingsServiceRepositoryImpl.TAG,
                'RecordingsServiceRepositoryImpl::fetchRecordingIdRecordingOptions',
                error,
            );
            throw error;
        }
    }

    /**
     * @inheritdoc
     */
    public async fetchRecordingValues(
        contentId: string,
        recordingId: string,
    ): Promise<TRecordingValues> {
        const requestParameters: RecordingsScheduleGetRequest = {
            ...this.getCommonRequestParams(),
            contentId,
            recordingId,
        };

        try {
            const response = await this.mRecordingApi.recordingsScheduleGet(requestParameters);
            return { recordingValues: response.data };
        } catch (error: any) {
            Log.e(
                RecordingsServiceRepositoryImpl.TAG,
                'RecordingsServiceRepositoryImpl::fetchRecordingValues',
                error,
            );
            throw error;
        }
    }

    /**
     * @inheritdoc
     */
    public async recordingRecovery(recordingId: string) {
        const requestParameters: RecordingsRecoveryPutRequest = {
            ...this.getCommonRequestParams(),
            recordingId,
        };

        try {
            return await this.mRecordingApi.recordingsRecoveryPut(requestParameters);
        } catch (error: any) {
            Log.e(
                RecordingsServiceRepositoryImpl.TAG,
                'RecordingsServiceRepositoryImpl::recordingRecovery',
                error,
            );
            throw error;
        }
    }

    /**
     * @inheritdoc
     */
    public async updateRecordingsLastViewedPosition(
        recordingId: string,
        lastViewedPosition: number,
    ) {
        const requestParameters: LastViewedPositionPutRequest = {
            ...this.getCommonRequestParams(),
            lastViewedPosition: { lastViewedPosition },
            recordingId,
        };

        try {
            return await this.mRecordingApi.lastViewedPositionPut(requestParameters);
        } catch (error: any) {
            Log.e(
                RecordingsServiceRepositoryImpl.TAG,
                'RecordingsServiceRepositoryImpl::lastViewedPositionPut',
                error,
            );
            throw error;
        }
    }

    /**
     * @inheritdoc
     */
    public async updateRecordingOfferIdSchedule(
        contentId: string,
        offerId: string,
        tRecordingValues: TRecordingValues,
    ) {
        const requestParameters: RecordingsOfferIdSchedulePutRequest = {
            ...this.getCommonRequestParams(),
            contentId,
            offerId,
            recordingValues: tRecordingValues.recordingValues,
        };

        try {
            return await this.mRecordingApi.recordingsOfferIdSchedulePut(requestParameters);
        } catch (error: any) {
            Log.e(
                RecordingsServiceRepositoryImpl.TAG,
                'RecordingsServiceRepositoryImpl::updateRecordingOfferIdSchedule',
                error,
            );
            throw error;
        }
    }

    /**
     * @inheritdoc
     */
    public async updateRecordingSchedule(
        contentId: string,
        recordingId: string,
        tRecordingValues: TRecordingValues,
    ) {
        const requestParameters: RecordingsSchedulePutRequest = {
            ...this.getCommonRequestParams(),
            contentId,
            recordingId,
            recordingValues: tRecordingValues.recordingValues,
        };

        try {
            return await this.mRecordingApi.recordingsSchedulePut(requestParameters);
        } catch (error: any) {
            Log.e(
                RecordingsServiceRepositoryImpl.TAG,
                'RecordingsServiceRepositoryImpl::updateRecordingSchedule',
                error,
            );
            throw error;
        }
    }
}
